import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Users, Home, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import FaqSection from '../components/FaqSection';

const PropertyManagementMallorcaPage = () => {
  const { language, t } = useLanguage();
  const content = t.propertyManagementMallorca;

  const services = [
    { icon: Home, title: content.services.items[0].title, desc: content.services.items[0].desc },
    { icon: Shield, title: content.services.items[1].title, desc: content.services.items[1].desc },
    { icon: Users, title: content.services.items[2].title, desc: content.services.items[2].desc },
  ];

  return (
    <div data-testid="property-management-mallorca-page">
      <Helmet>
        <title>Mallorca Property Management | Balearic Home Solutions</title>
        <meta name="description" content="Professional Mallorca property management for international homeowners. Local oversight, maintenance coordination and transparent reporting. Based in Calvià, serving Bendinat, Sol de Mallorca and Southwest Mallorca." />
        <link rel="canonical" href="https://balearichomesolutions.com/property-management-mallorca" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {content.hero.label}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {content.hero.title}
            </h1>
            <p className="text-navy/80 text-xl md:text-2xl font-medium mb-4">
              {content.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                {content.hero.cta}
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
                {content.introduction.title}
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p className="text-navy font-medium">{content.introduction.introLine}</p>
                <p>{content.introduction.text1}</p>
                <p>{content.introduction.text2}</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80"
                alt="Luxury villa in Mallorca"
                className="w-full h-auto object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-navy/90 p-6">
                <div className="flex items-center gap-3">
                  <MapPin size={20} className="text-white/70" />
                  <div>
                    <p className="text-white font-medium">{content.introduction.locationLabel}</p>
                    <p className="text-white/70 text-sm">Balearic Islands · Spain</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {content.services.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
              {content.services.title}
            </h2>
            <p className="text-stone-600 leading-relaxed">
              {content.services.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border-t-2 border-navy"
              >
                <service.icon size={32} className="text-navy mb-4" />
                <h3 className="font-heading text-xl text-navy mb-3">{service.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-2 text-navy font-medium hover:underline underline-offset-4"
            >
              {content.services.viewAll}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>




      {/* Managing a Property in Mallorca */}
      <section data-testid="pm-managing-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-14"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {content.managing.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
              {content.managing.title}
            </h2>
            <div className="space-y-4 text-stone-600 leading-relaxed">
              <p>{content.managing.intro1}</p>
              <p>{content.managing.intro2}</p>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {content.managing.items.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="border-t border-stone-200 pt-5"
              >
                <h3 className="font-heading text-lg text-navy mb-2">{item.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Our Property Management Works */}
      <section data-testid="pm-process-section" className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {content.process.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
              {content.process.title}
            </h2>
            <p className="text-stone-600 leading-relaxed">
              {content.process.description}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {content.process.steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-white p-6 border-t-2 border-navy"
              >
                <span className="text-xs font-bold tracking-widest text-stone-400">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="font-heading text-lg text-navy mt-3 mb-2 leading-snug">{step.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section data-testid="pm-who-we-work-section" className="py-16 md:py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-10"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-4">
              {content.whoWeWork.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-4">
              {content.whoWeWork.title}
            </h2>
            <p className="text-white/70 leading-relaxed">
              {content.whoWeWork.description}
            </p>
          </motion.div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 max-w-4xl">
            {content.whoWeWork.types.map((type) => (
              <li key={type} className="flex items-start gap-3 text-white/80 leading-relaxed">
                <span className="w-1 h-1 bg-white/60 rounded-full mt-2.5 flex-shrink-0" />
                <span>{type}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection content={content.faq} testId="pm-mallorca-faq" />

      {/* Closing / CTA Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
              {content.closing.title}
            </h2>
            <p className="text-stone-600 leading-relaxed mb-10">
              {content.closing.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="closing-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                {content.closing.ctaPrimary}
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagementMallorcaPage;
